import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../lib/api";
import Masthead from "../components/Masthead";
import Kpi from "../components/Kpi";
import Panel from "../components/Panel";
import { fmtNum, statusClass, fmtSecAgo } from "../lib/format";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

export default function SOC() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    apiGet("/soc/summary").then(setD);
  }, []);
  if (!d) return <div className="p-8 opacity-60">Loading…</div>;

  const k = d.kpis;

  return (
    <>
      <Masthead
        module="soc"
        eyebrow="Security Operations Centre"
        title="SOC Operations"
        subtitle="24/7 shift-based operations · detection, response & SLA attainment by severity"
        meta={<div>{d.shift}<br />as at {d.as_at}</div>}
      />
      <div className="px-[26px] py-5 flex-1">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="soc-active" label="Active incidents now" value={k.active_incidents} sub={`${k.critical} critical · ${k.high} high · ${k.medium} medium`} alert onClick={() => nav("/drill/incidents?status=Open")} />
          <Kpi testid="soc-sla" label="Overall SLA attainment" value={`${k.sla_attainment}%`} sub="rolling 30 days · target ≥ 95%" trend={{ delta_pct: +1.4, direction: "up", period: "MoM", is_improving: true }} onClick={() => nav("/drill/incidents")} />
          <Kpi testid="soc-mttr" label="Mean time to respond" value={`${k.mttr_min} min`} sub="detect → containment" trend={d.speed?.trends?.mttr} onClick={() => nav("/drill/incidents")} />
          <Kpi testid="soc-backlog" label="Triage backlog" value={k.triage_backlog} sub="alerts awaiting pickup" alert trend={{ delta_pct: -21, direction: "down", period: "since 08:00", is_improving: true }} onClick={() => nav("/drill/alerts")} />
        </div>

        <div className="cv-section-label">Detection &amp; Response Speed · Momentum</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Kpi testid="soc-mttd" label="MTTD — mean time to detect" value={`${d.speed.mttd_min} min`} sub="signal → alert" trend={d.speed.trends.mttd} onClick={() => nav("/drill/alerts")} />
          <Kpi testid="soc-mtta" label="MTTA — mean time to ack" value={`${d.speed.mtta_min} min`} sub="alert → analyst pickup" trend={d.speed.trends.mtta} onClick={() => nav("/drill/alerts")} />
          <Kpi testid="soc-mttr2" label="MTTR — mean time to respond" value={`${d.speed.mttr_min} min`} sub="detect → containment" trend={d.speed.trends.mttr} onClick={() => nav("/drill/incidents")} />
          <Kpi testid="soc-dwell" label="Adversary dwell time" value={`${d.speed.dwell_hrs} hrs`} sub="intrusion → eviction" trend={d.speed.trends.dwell} onClick={() => nav("/drill/incidents")} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <Panel testid="panel-funnel" title="Alert Funnel (24h)" subtitle="From raw signal to confirmed incident">
            <div className="flex flex-col gap-2">
              {d.funnel.map((f, i) => (
                <div key={f.label} className="flex items-center gap-2">
                  <div className="text-[12px] small-caps w-32" style={{ color: "var(--cv-charcoal-soft)" }}>{f.label}</div>
                  <div className="flex-1 h-3 rounded" style={{ background: "var(--cv-line)" }}>
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${Math.max(2, (f.value / d.funnel[0].value) * 100)}%`,
                        background: `var(--cv-${i < 2 ? "slate" : i < 4 ? "amber" : "crimson"})`,
                      }}
                    />
                  </div>
                  <div className="text-[12px] font-bold w-20 text-right tabular-nums">{fmtNum(f.value)}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel testid="panel-alert-hourly" title="Alert Volume by Hour" subtitle="Last 24 hours">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.alert_hourly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7DCCD" />
                  <XAxis dataKey="h" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="alerts" fill="#A5300F" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel testid="panel-categories" title="Incident Categories" subtitle="Share of confirmed incidents (30d)">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.categories} dataKey="pct" nameKey="label" innerRadius={40} outerRadius={75} paddingAngle={1}>
                    {d.categories.map((c) => <Cell key={c.label} fill={c.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[13px] mt-2" style={{ color: "var(--cv-charcoal-soft)" }}>
              {d.categories.map((c) => `${c.label} ${c.pct}%`).join(" · ")}
            </div>
          </Panel>
        </div>

        <Panel testid="panel-sla" title="SLA Attainment by Severity" subtitle="Response & containment commitments met, rolling 30 days">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                <th className="text-left px-3 py-2 small-caps font-bold">Severity</th>
                <th className="text-left px-3 py-2 small-caps font-bold">SLA commitment</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Cases</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Breached</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Attainment</th>
              </tr>
            </thead>
            <tbody>
              {d.sla_by_severity.map((s, i) => (
                <tr key={s.severity} className="cv-row-clickable" data-testid={`sla-row-${i}`} onClick={() => nav(`/drill/incidents?severity=${s.severity.split(" ")[0]}`)}>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{s.severity}</strong></td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{s.commitment}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{s.cases}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{s.breached}</td>
                  <td className="px-3 py-2 border-b font-bold" style={{ borderColor: "var(--cv-line)", color: s.attainment >= 95 ? "var(--cv-green)" : "var(--cv-crimson)" }}>{s.attainment}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <div className="cv-section-label mt-6">Analysts on Shift</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {d.analysts.map((a) => (
            <button
              key={a.id}
              data-testid={`analyst-${a.id}`}
              onClick={() => nav(`/drill/incidents?assignee=${encodeURIComponent(a.name)}`)}
              className="cv-panel text-left hover:shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-white"
                  style={{ background: "var(--cv-slate)" }}
                >
                  {a.initials}
                </div>
                <div>
                  <div className="font-bold">{a.name}</div>
                  <div className="text-[13px]" style={{ color: "var(--cv-charcoal-soft)" }}>{a.role}</div>
                </div>
              </div>
              <div className="text-[12px] mt-3" style={{ color: "var(--cv-charcoal)" }}>
                <strong>{a.status}</strong> — {a.case}
              </div>
              <div className="flex justify-between mt-2 text-[13px]" style={{ color: "var(--cv-charcoal-soft)" }}>
                <span>Active cases <strong style={{ color: "var(--cv-charcoal)" }}>{a.active_cases}</strong></span>
                <span>Load <strong style={{ color: a.load > 80 ? "var(--cv-crimson)" : "var(--cv-green)" }}>{a.load}%</strong></span>
              </div>
            </button>
          ))}
        </div>

        <div className="cv-section-label mt-6">Log Source Health</div>
        <Panel testid="panel-logsources" title="Telemetry feeds reporting into the SIEM" subtitle="Silent sources are detection blind spots">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--cv-sand)", color: "var(--cv-charcoal-soft)" }}>
                <th className="text-left px-3 py-2 small-caps font-bold">Log source</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Type</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Events / sec</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Last event</th>
                <th className="text-left px-3 py-2 small-caps font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.log_sources.map((l, i) => (
                <tr key={l.id} className="cv-row-clickable" data-testid={`logsource-${i}`}>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}><strong>{l.source}</strong></td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{l.type}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{l.eps.toLocaleString()}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>{fmtSecAgo(l.last_event_sec)}</td>
                  <td className="px-3 py-2 border-b" style={{ borderColor: "var(--cv-line)" }}>
                    <span className={`status-pill ${statusClass(l.status)}`}>{l.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
}
